/**
 * The head of a comanda: whose table it is, how long it has been cooking, and
 * the two things you can do to the whole ticket — open it across the screen, or
 * cancel it.
 *
 * The table name is the heading and the daily number is a small reference. A
 * cook thinks in tables; the number is for the till.
 */
import { Clock, Flame, Maximize2, X } from 'lucide-react';
import { formatTimer } from '@shared/utils/time';
import { formatDailyNumber } from '@shared/utils/format';
import { useTranslation } from '../i18n';
import type { KdsT } from '../lib/i18n/settings';

interface OrderCardHeaderProps {
  order: any;
  elapsed: number;
  showTimer: boolean;
  isOverdue: boolean;
  isWarning: boolean;
  /** Opens this comanda across the whole display. */
  onOpenFull?: () => void;
  onRequestCancel?: () => void;
  /**
   * Feature 257 — inside a station card the head is overhead, not content.
   * At 44 px the table name plus its gap took 58 px of a card that has about
   * 230 px to give, which is why the dishes sat below the fold on the 15-inch
   * till. Full-screen and by-table keep the large heading.
   */
  compact?: boolean;
}

export function orderTitle(order: any, t: KdsT): string {
  if (order?.tableName) return order.tableName;
  if (order?.tableNumber) return t('kds.order.tableNumber', 'Tavolo {{number}}', { number: order.tableNumber });
  return t('kds.order.takeaway', 'Asporto');
}

export default function OrderCardHeader({
  order,
  elapsed,
  showTimer,
  isOverdue,
  isWarning,
  onOpenFull,
  onRequestCancel,
  compact,
}: OrderCardHeaderProps) {
  const { t } = useTranslation();
  const cancellable = order?.status !== 'COMPLETED' && order?.status !== 'CANCELLED';

  return (
    <div
      className={['flex items-center justify-between gap-3', compact ? 'mb-1.5' : 'mb-2'].join(' ')}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span
          className={[
            compact ? 'text-kds-station' : 'text-kds-title',
            // The table name is what the cook reads to know where the plates
            // go, and it was the only thing in this row allowed to shrink:
            // everything beside it is `shrink-0`, so in a station column it
            // collapsed to "T…" while a button spelled out the word "Apri".
            'text-kds-text truncate min-w-0 flex-1',
          ].join(' ')}
        >
          {orderTitle(order, t)}
        </span>
        <span
          className={[
            'text-kds-badge text-kds-muted font-mono shrink-0',
            // In a station column the number is what you say out loud to name a
            // comanda — it stays, without the box it does not need.
            compact ? '' : 'bg-kds-card px-2 py-1 rounded',
          ].join(' ')}
        >
          {/* `formatDailyNumber` already returns "#009"; a second "#" here
              printed "##009" on every card. */}
          {formatDailyNumber(order?.dailyNumber)}
        </span>
        {order?.priority > 0 && <Flame size={32} className="text-kds-priority shrink-0" />}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {/* Not in a station column. The wave row two lines below shows the same
            seconds, and carries the same colours since 16/09/2026 — two clocks
            with one number between them was costing the table name its room. */}
        {showTimer && !compact && (
          <span
            className={`text-kds-meta font-mono ${
              isOverdue ? 'text-kds-cancelled' : isWarning ? 'text-kds-preparing' : 'text-kds-muted'
            }`}
            title={t('kds.order.waveTimer', 'Tempo della portata in preparazione')}
          >
            <Clock size={22} className="inline mr-1" />
            {formatTimer(elapsed)}
          </span>
        )}

        {onOpenFull && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenFull();
            }}
            className={[
              'inline-flex items-center justify-center gap-2 rounded-lg bg-kds-card text-kds-text text-kds-action',
              'min-h-touch min-w-touch hover:bg-kds-card/70 transition-colors',
              compact ? 'px-3' : 'px-4',
            ].join(' ')}
            title={t('kds.order.openFull', 'Apri la comanda a tutto schermo')}
            aria-label={t('kds.order.openFull', 'Apri la comanda a tutto schermo')}
          >
            <Maximize2 size={26} strokeWidth={3} />
            {/* The word is worth its width on a full card; in a station column
                the same pixels are the difference between "Tavolo 12" and
                "T…". The icon and the title say it either way. */}
            {!compact && t('kds.order.open', 'Apri')}
          </button>
        )}

        {cancellable && onRequestCancel && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRequestCancel();
            }}
            className="text-red-400 hover:text-white hover:bg-red-600 transition-colors p-3 rounded-lg border border-red-400/40 hover:border-red-600 min-h-touch min-w-touch"
            title={t('kds.order.cancelTitle', 'ANNULLA ordine (apre conferma)')}
            aria-label={t('kds.order.cancelAria', 'Annulla ordine')}
          >
            <X size={26} strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  );
}
